import React from 'react';
import { useFormikContext } from 'formik';
import { Grid, Typography } from '@mui/material';
//
import { FormikField, FormikPhone } from '@components/formik';
import { Api, files, getFile, getToken, uploadFile } from '@/utils';
//
import ObTooltip from './ObTooltip';
import { AppUpload, CropUpload } from '../upload';
import { useAuthContext } from '../auth';

const PublicDetails: React.FC = () => {
  const [imgSrc, setImgSrc] = React.useState('');
  const [logoState, setLogoState] = React.useState({
    isLoading: false,
    isError: false,
    message: '',
    isSuccess: false,
  });
  const [profileState, setProfileState] = React.useState({
    isLoading: false,
    isError: false,
    message: '',
    isSuccess: false,
  });
  const [logo, setLogo] = React.useState<File | any>(null);
  const [croppedLogo, setCroppedLogo] = React.useState<any>(null);
  const [croppedProfile, setCroppedProfile] = React.useState<any>(null);
  const [profile, setProfile] = React.useState<File | any>(null);
  const [documents, setDocuments] = React.useState<any>({
    logo: '',
    profile_logo: '',
  });
  const [openCrop, setOpenCrop] = React.useState(false);
  const [openProfileCrop, setOpenProfileCrop] = React.useState(false);

  // Get OB data from AuthContext
  const { obData } = useAuthContext();
  const { values, setFieldValue } = useFormikContext<any>();
  const token = getToken();

  // console.log(obData);
  // console.log(obData?.id);

  const _LFile = React.useCallback(async () => {
    const file = await getFile(obData.logo);
    if (file) {
      setLogo(file);
      localStorage.setItem('logo', JSON.stringify(file));
    }
  }, [obData.logo]);

  const _PlFile = React.useCallback(async () => {
    const file = await getFile(obData.profileLogo);
    if (file) {
      setProfile(file);
      localStorage.setItem('profileLogo', JSON.stringify(file));
    }
  }, [obData.profileLogo]);

  React.useEffect(() => {
    const localLogo = localStorage.getItem('logo');
    const localProfileLogo = localStorage.getItem('profileLogo');
    localLogo && setLogo(JSON.parse(localLogo));
    localProfileLogo && setProfile(JSON.parse(localProfileLogo));

    !localLogo && obData.logo && _LFile();
    !localProfileLogo && obData.profileLogo && _PlFile();
  }, [_LFile, _PlFile]);

  React.useEffect(() => {
    if (documents.logo !== '') {
      setFieldValue('logo', documents.logo);
    }

    if (documents.profile_logo !== '') {
      setFieldValue('profileLogo', documents.profile_logo);
    }

    if (documents.logo !== '' && documents.profile_logo !== '') {
      Api.put('/onboarding', values, {
        headers: { Authorization: `Bearer ${token}` },
      });
    }
  }, [documents, values.logo, values.profileLogo]);

  const handleDrop = React.useCallback(async (acceptedFiles: File[]) => {
    const newFile = acceptedFiles[0];
    if (newFile) {
      console.log(newFile);
      setImgSrc(URL.createObjectURL(newFile));
      setOpenCrop(true);
    }
  }, []);

  const handleDropProfile = React.useCallback(async (acceptedFiles: File[]) => {
    const newFile = acceptedFiles[0];
    if (newFile) {
      setImgSrc(URL.createObjectURL(newFile));
      setOpenProfileCrop(true);
    }
  }, []);

  const handleConfirmLogo = async () => {
    setLogoState({
      isLoading: true,
      isError: false,
      message: '',
      isSuccess: false,
    });
    console.log({ croppedLogo });
    if (croppedLogo) {
      // blob to file
      // const newFile = files.blobToFile(croppedLogo, 'logo.png');
      const blob = await fetch(croppedLogo).then((response) => response.blob());
      const rawFile = new File([blob], 'image.jpg', {
        type: 'image/jpeg',
      });
      console.log({ rawFile });
      const newFile = await files.imgReducer(rawFile, 0.8);
      console.log({ newFile });
      if (newFile) {
        setLogo(
          Object.assign(newFile, {
            preview: URL.createObjectURL(newFile as Blob),
          })
        );

        // Save the same file in localStorage
        localStorage.setItem('logo', JSON.stringify(newFile));

        try {
          // Check for max size
          const isMax = await files.isMaxSize(newFile as File, 5);
          if (isMax) {
            setLogoState({
              isLoading: false,
              isError: true,
              message: 'Image must be less than 5MB',
              isSuccess: false,
            });
            return;
          }

          const { key } = await uploadFile(newFile as File, obData?.id, 'logo');
          if (key) {
            setDocuments({ ...documents, logo: key });

            setLogoState({
              isLoading: false,
              isError: false,
              message: 'File uploaded successfully',
              isSuccess: true,
            });
          }
        } catch (error) {
          console.log(error);

          // Remove the file from localStorage
          localStorage.removeItem('logo');

          setLogoState({
            isLoading: false,
            isError: true,
            message: "Couldn't upload file",
            isSuccess: false,
          });
        }
      }
    }
    setOpenCrop(false);
  };

  const handleConfirmProfile = async () => {
    setProfileState({
      isLoading: true,
      isError: false,
      message: '',
      isSuccess: false,
    });
    if (croppedProfile) {
      // blob to file
      const blob = await fetch(croppedProfile).then((response) =>
        response.blob()
      );
      const rawFile = new File([blob], 'image.jpg', {
        type: 'image/jpeg',
      });
      const newFile = await files.imgReducer(rawFile, 0.7);
      if (newFile) {
        setProfile(
          Object.assign(newFile, {
            preview: URL.createObjectURL(newFile as Blob),
          })
        );

        // Save the same file in localStorage
        localStorage.setItem('profileLogo', JSON.stringify(newFile));

        try {
          // Check for square image
          const isSquare = await files.isSquare(newFile as File);

          if (!isSquare) {
            setProfileState({
              isLoading: false,
              isError: true,
              message: 'Image must be square',
              isSuccess: false,
            });
            return;
          }

          // Check for max size
          const isMax = await files.isMaxSize(newFile as File, 5);
          if (isMax) {
            setProfileState({
              isLoading: false,
              isError: true,
              message: 'Image must be less than 5MB',
              isSuccess: false,
            });
            return;
          }

          const { key } = await uploadFile(
            newFile as File,
            obData?.id,
            'profile_logo'
          );
          if (key) {
            setDocuments({ ...documents, profile_logo: key });

            console.log({ key });

            setProfileState({
              isLoading: false,
              isError: false,
              message: 'File uploaded successfully',
              isSuccess: true,
            });
          }
        } catch (error) {
          console.log(error);

          // Remove the file from localStorage
          localStorage.removeItem('profileLogo');

          setProfileState({
            isLoading: false,
            isError: true,
            message: "Couldn't upload file",
            isSuccess: false,
          });
        }
      }
    }
    setOpenProfileCrop(false);
  };

  return (
    <>
      <Typography variant="h4" component="h2" gutterBottom>
        Add public details for customers
      </Typography>
      <Typography variant="body1" gutterBottom color="text.secondary">
        This information may be visible in payment statements, invoices, and
        receipts.
      </Typography>

      <ObTooltip
        title={
          <>
            <img src="/images/business-name.png" alt="statement descriptor" />
            <Typography variant="body2">
              Your statement descriptor shows up on your customers’ bank or
              credit card statements. It should be 5-22 characters.
            </Typography>
          </>
        }
      >
        <FormikField
          name="statementDescriptor"
          label={`Statement descriptor (${values.statementDescriptor.length}/22)`}
          placeholder="Your business name"
          // inputProps={{ maxLength: 22 }}
        />
      </ObTooltip>

      <FormikPhone name="supportPhone" label="Customer support phone number" />

      <FormikField
        name="supportUrl"
        label="Customer support website"
        placeholder="https://example.com"
      />

      <FormikField
        name="supportEmail"
        label="Customer support email address"
        placeholder="example@email.com"
        type="email"
      />

      <ObTooltip
        title={
          <>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Email Banner:</strong>
            </Typography>
            <Typography variant="body2">
              <strong>File types:</strong> jpg/jpeg, png, svg
            </Typography>
            <Typography variant="body2">
              <strong>Size:</strong> less than 5mb
            </Typography>
            <Typography variant="body2">
              <strong>Dimensions:</strong> anything
            </Typography>
            <Typography variant="subtitle2" gutterBottom mt={3}>
              <strong>Profile Logo:</strong>
            </Typography>
            <Typography variant="body2">
              <strong>File types:</strong> jpg/jpeg, png, svg
            </Typography>
            <Typography variant="body2">
              <strong>Size:</strong> less than 5mb
            </Typography>
            <Typography variant="body2">
              <strong>Dimensions:</strong>must be square
            </Typography>
          </>
        }
      >
        <Grid container mt={1} spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" gutterBottom>
              Email Banner
            </Typography>
            <AppUpload
              error={logoState.isError}
              success={logoState.isSuccess}
              disabled={logoState.isLoading}
              file={logo}
              onDrop={handleDrop}
              onDelete={() => {
                setLogo(null);
                localStorage.removeItem('logo');
                setLogoState({
                  isLoading: false,
                  isError: false,
                  message: '',
                  isSuccess: false,
                });
              }}
              accept={{ 'image/png': ['.png', '.jpg', '.jpeg'] }}
            />
            {logoState.message && (
              <Typography
                variant="body2"
                style={{
                  marginTop: 5,
                  color: logoState.isError ? '#f44336' : '#4caf50',
                  lineHeight: '1.1',
                }}
                gutterBottom
              >
                <small>{logoState.message}</small>
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" gutterBottom>
              Profile Logo
            </Typography>
            <AppUpload
              error={profileState.isError}
              success={profileState.isSuccess}
              disabled={profileState.isLoading}
              file={profile}
              onDrop={handleDropProfile}
              onDelete={() => {
                setProfile(null);
                localStorage.removeItem('profileLogo');
                setProfileState({
                  isLoading: false,
                  isError: false,
                  message: '',
                  isSuccess: false,
                });
              }}
              accept={{ 'image/png': ['.png', '.jpg', '.jpeg'] }}
            />
            {profileState.message && (
              <Typography
                variant="body2"
                style={{
                  marginTop: 5,
                  color: profileState.isError ? '#f44336' : '#4caf50',
                  lineHeight: '1.1',
                }}
                gutterBottom
              >
                <small>{profileState.message}</small>
              </Typography>
            )}
          </Grid>
        </Grid>
      </ObTooltip>

      <CropUpload
        imgSrc={imgSrc}
        open={openCrop}
        cropped={croppedLogo}
        setCropped={setCroppedLogo}
        aspect={undefined}
        setOpen={setOpenCrop}
        onConfirm={handleConfirmLogo}
      />
      <CropUpload
        imgSrc={imgSrc}
        open={openProfileCrop}
        cropped={croppedProfile}
        setCropped={setCroppedProfile}
        aspect={1}
        setOpen={setOpenProfileCrop}
        onConfirm={handleConfirmProfile}
      />
    </>
  );
};

export default PublicDetails;
